// ============================================================================
// 자동화 규칙 생성/편집 모달 (CHG-047: REQ-V47-013)
// ============================================================================

import { useState } from 'react';
import { X, Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCreateRule, useUpdateRule } from '../../hooks/useAutomation';
import type { AutomationRule } from '../../services/automationService';
import type { WorkflowStatusInfo, ProjectMember } from '../../services/projectService';

interface Props {
  projectId: string;
  ruleId: string | null; // null = new
  rules: AutomationRule[];
  statuses: WorkflowStatusInfo[];
  members: ProjectMember[];
  onClose: () => void;
}

const EVENTS = [
  { value: 'BRANCH_CREATED', label: '브랜치 생성' },
  { value: 'PR_OPENED', label: 'PR 생성' },
  { value: 'PR_REVIEW_REQUESTED', label: '리뷰 요청' },
  { value: 'PR_CHANGES_REQUESTED', label: '변경 요청' },
  { value: 'PR_APPROVED', label: '리뷰 승인' },
  { value: 'PR_MERGED', label: 'PR 머지' },
  { value: 'PR_CLOSED', label: 'PR 닫힘' },
];

const FIELD_OPTIONS: Record<string, { value: string; label: string }[]> = {
  pr: [
    { value: 'pr.isDraft', label: 'Draft 여부' },
    { value: 'pr.sourceBranch', label: '소스 브랜치' },
    { value: 'pr.targetBranch', label: '타겟 브랜치' },
    { value: 'pr.ciStatus', label: 'CI 상태' },
    { value: 'pr.hasConflicts', label: '충돌 여부' },
    { value: 'pr.reviewsAllApproved', label: '전체 승인 여부' },
    { value: 'pr.workflowConclusion', label: 'CI 결론 (Phase 3)' },
  ],
  branch: [
    { value: 'branch.name', label: '브랜치명' },
  ],
  issue: [
    { value: 'issue.statusCategory', label: '이슈 상태 카테고리' },
    { value: 'issue.issueType', label: '이슈 유형' },
    { value: 'issue.priority', label: '우선순위' },
    { value: 'issue.testAllPassed', label: '테스트 전체 통과' },
  ],
  release: [
    { value: 'release.status', label: '릴리즈 상태' },
  ],
};

const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'in', label: '포함 (in)' },
  { value: 'isTrue', label: 'True' },
  { value: 'isFalse', label: 'False' },
];

const ACTION_TYPES = [
  { value: 'CHANGE_STATUS', label: '상태 변경' },
  { value: 'ADD_COMMENT', label: '코멘트 추가' },
  { value: 'ASSIGN_USER', label: '담당자 변경' },
  { value: 'ADD_LABEL', label: '라벨 추가' },
  { value: 'SEND_NOTIFICATION', label: '알림 전송' },
  { value: 'UPDATE_RELEASE_STATUS', label: '릴리즈 상태 변경' },
  { value: 'GENERATE_RELEASE_NOTES', label: '릴리즈 노트 생성' },
];

type Condition = { field: string; operator: string; value: string };
type Action = { type: string; params: Record<string, string> };

export default function RuleEditModal({ projectId, ruleId, rules, statuses, members, onClose }: Props) {
  const existing = ruleId ? rules.find((r) => r.ruleId === ruleId) : null;
  const isNew = !existing;

  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [triggerEvent, setTriggerEvent] = useState(existing?.triggerEvent ?? 'PR_OPENED');
  const [conditions, setConditions] = useState<Condition[]>(
    (existing?.conditions as Condition[]) ?? [],
  );
  const [actions, setActions] = useState<Action[]>(
    (existing?.actions as Action[]) ?? [{ type: 'CHANGE_STATUS', params: { statusId: '' } }],
  );
  const [priority, setPriority] = useState(existing?.priority ?? 0);
  const [warnings, setWarnings] = useState<string[]>([]);

  const createMutation = useCreateRule(projectId);
  const updateMutation = useUpdateRule(projectId);
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Available fields based on trigger event
  const isPREvent = triggerEvent.startsWith('PR_');
  const availableFields = [
    ...(isPREvent ? FIELD_OPTIONS.pr : FIELD_OPTIONS.branch),
    ...FIELD_OPTIONS.issue,
    ...FIELD_OPTIONS.release,
  ];

  const addCondition = () => setConditions([...conditions, { field: availableFields[0]?.value ?? '', operator: 'eq', value: '' }]);
  const removeCondition = (i: number) => setConditions(conditions.filter((_, idx) => idx !== i));
  const updateCondition = (i: number, patch: Partial<Condition>) => setConditions(conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const addAction = () => setActions([...actions, { type: 'CHANGE_STATUS', params: { statusId: '' } }]);
  const removeAction = (i: number) => setActions(actions.filter((_, idx) => idx !== i));
  const updateAction = (i: number, patch: Partial<Action>) => setActions(actions.map((a, idx) => idx === i ? { ...a, ...patch } : a));

  const handleSave = async () => {
    if (!name.trim()) { toast.error('이름을 입력하세요.'); return; }
    if (actions.length === 0) { toast.error('최소 1개의 액션이 필요합니다.'); return; }

    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        triggerEvent,
        conditions: conditions.map((c) => ({
          field: c.field,
          operator: c.operator,
          value: c.operator === 'isTrue' || c.operator === 'isFalse' ? null : c.value || null,
        })),
        actions,
        priority,
      };

      if (isNew) {
        const result = await createMutation.mutateAsync(payload);
        setWarnings(result.warnings);
        if (result.warnings.length === 0) {
          toast.success('규칙이 생성되었습니다.');
          onClose();
        }
      } else {
        const result = await updateMutation.mutateAsync({ ruleId: ruleId!, data: payload });
        setWarnings(result.warnings);
        if (result.warnings.length === 0) {
          toast.success('규칙이 수정되었습니다.');
          onClose();
        }
      }
    } catch {
      toast.error('저장에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl mx-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-gray-900">{isNew ? '자동화 규칙 추가' : '규칙 편집'}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="size-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 space-y-1">
              {warnings.map((w, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="size-4 shrink-0 mt-0.5" />{w}</div>)}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
          </div>

          {/* WHEN: Trigger */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm font-semibold text-blue-600 mb-2">WHEN (트리거)</div>
            <select value={triggerEvent} onChange={(e) => setTriggerEvent(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500">
              {EVENTS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>

          {/* AND: Conditions */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-sm font-semibold text-amber-600 mb-2">AND (조건) — 모두 충족 시 실행</div>
            {conditions.map((cond, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <select value={cond.field} onChange={(e) => updateCondition(i, { field: e.target.value })} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm">
                  {availableFields.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select value={cond.operator} onChange={(e) => updateCondition(i, { operator: e.target.value })} className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm">
                  {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {cond.operator !== 'isTrue' && cond.operator !== 'isFalse' && (
                  <input value={cond.value} onChange={(e) => updateCondition(i, { value: e.target.value })} placeholder="값" className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                )}
                <button onClick={() => removeCondition(i)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="size-3.5" /></button>
              </div>
            ))}
            <button onClick={addCondition} className="text-sm text-amber-700 hover:underline flex items-center gap-1"><Plus className="size-3" /> 조건 추가</button>
          </div>

          {/* THEN: Actions */}
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-sm font-semibold text-green-600 mb-2">THEN (액션)</div>
            {actions.map((action, i) => (
              <div key={i} className="flex items-start gap-2 mb-2">
                <select value={action.type} onChange={(e) => updateAction(i, { type: e.target.value, params: {} })} className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm shrink-0">
                  {ACTION_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                <div className="flex-1">
                  {action.type === 'CHANGE_STATUS' && (
                    <select value={action.params.statusId ?? ''} onChange={(e) => updateAction(i, { params: { statusId: e.target.value } })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm">
                      <option value="">상태 선택</option>
                      {statuses.map((s) => <option key={s.statusId} value={s.statusId}>{s.name}</option>)}
                    </select>
                  )}
                  {action.type === 'ADD_COMMENT' && (
                    <input value={action.params.message ?? ''} onChange={(e) => updateAction(i, { params: { message: e.target.value } })} placeholder="코멘트 메시지" className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                  )}
                  {action.type === 'ASSIGN_USER' && (
                    <select value={action.params.userId ?? ''} onChange={(e) => updateAction(i, { params: { userId: e.target.value } })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm">
                      <option value="">멤버 선택</option>
                      {members.map((m) => <option key={m.userId} value={m.userId}>{m.user.name}</option>)}
                    </select>
                  )}
                  {action.type === 'ADD_LABEL' && (
                    <input value={action.params.label ?? ''} onChange={(e) => updateAction(i, { params: { label: e.target.value } })} placeholder="라벨명" className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                  )}
                  {action.type === 'SEND_NOTIFICATION' && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-[11px]">
                          <input type="checkbox" checked={(action.params.channels as string ?? '').includes('email')} onChange={(e) => {
                            const cur = (action.params.channels as string ?? '').split(',').filter(Boolean);
                            const next = e.target.checked ? [...cur, 'email'] : cur.filter((c) => c !== 'email');
                            updateAction(i, { params: { ...action.params, channels: next.join(',') } });
                          }} className="rounded" /> 이메일
                        </label>
                        <label className="flex items-center gap-1 text-[11px]">
                          <input type="checkbox" checked={(action.params.channels as string ?? '').includes('slack')} onChange={(e) => {
                            const cur = (action.params.channels as string ?? '').split(',').filter(Boolean);
                            const next = e.target.checked ? [...cur, 'slack'] : cur.filter((c) => c !== 'slack');
                            updateAction(i, { params: { ...action.params, channels: next.join(',') } });
                          }} className="rounded" /> Slack
                        </label>
                      </div>
                      <select value={action.params.recipients ?? 'pm'} onChange={(e) => updateAction(i, { params: { ...action.params, recipients: e.target.value } })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                        <option value="assignee">담당자</option>
                        <option value="reviewer">리뷰어</option>
                        <option value="pm">PM</option>
                        <option value="all">전체</option>
                      </select>
                      <input value={action.params.messageTemplate ?? ''} onChange={(e) => updateAction(i, { params: { ...action.params, messageTemplate: e.target.value } })} placeholder="메시지 ({issueKey}, {prNumber}, {actor})" className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                    </div>
                  )}
                  {action.type === 'UPDATE_RELEASE_STATUS' && (
                    <select value={action.params.targetStatus ?? ''} onChange={(e) => updateAction(i, { params: { targetStatus: e.target.value } })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm">
                      <option value="">상태 선택</option>
                      <option value="PLANNING">계획</option>
                      <option value="IN_PROGRESS">진행 중</option>
                      <option value="TESTING">테스트</option>
                      <option value="READY">준비 완료</option>
                      <option value="RELEASED">릴리즈됨</option>
                    </select>
                  )}
                  {action.type === 'GENERATE_RELEASE_NOTES' && (
                    <span className="text-[11px] text-gray-400">Markdown 형식으로 릴리즈 노트를 자동 생성합니다.</span>
                  )}
                </div>
                <button onClick={() => removeAction(i)} className="p-1 text-gray-400 hover:text-red-500 mt-1"><Trash2 className="size-3.5" /></button>
              </div>
            ))}
            <button onClick={addAction} className="text-sm text-green-700 hover:underline flex items-center gap-1"><Plus className="size-3" /> 액션 추가</button>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">우선순위 (낮을수록 먼저 실행)</label>
            <input type="number" min={0} value={priority} onChange={(e) => setPriority(parseInt(e.target.value, 10) || 0)} className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
          <button onClick={handleSave} disabled={isPending} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50">
            {isPending && <Loader2 className="size-4 animate-spin" />}
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
